import React from "react";
import { Box, Divider, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { AutocompleteInput } from "_components";
import CustomDateTimePicker from "_components/CustomDateTimePicker";

// ─── Section label style ──────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <Typography
    sx={{
      fontWeight: 700,
      textTransform: "uppercase",
      color: "#1a3a5c",
    }}
  >
    {children}
  </Typography>
);

// ── Darker text for disabled/read-only fields ──────────────────────────────
// AutocompleteInput / CustomDateTimePicker render MUI inputs internally, so
// rather than touching their source we target the real DOM class
// (.Mui-disabled) via a descendant selector on a wrapping Box. This still
// works no matter how deeply nested the actual <input> ends up being.
// WebkitTextFillColor is included because Chrome/Safari paint disabled
// input text using that property instead of `color`.
const disabledFieldSx = {
  "& .Mui-disabled": {
    color: "#1a3a5c !important",
    WebkitTextFillColor: "#1a3a5c !important",
  },
  "& .MuiInputBase-input.Mui-disabled": {
    color: "#1a3a5c !important",
    WebkitTextFillColor: "#1a3a5c !important",
  },
};

// ─── Component ────────────────────────────────────────────────────────────────
const ScheduledInterruptionRow = ({
  index,
  siCount,
  isLast,
  control,
  trigger,
  watch,
  setValue,
  balancingCurtailment,
  siTypes,
  onAdd,
  onRemove,
  curtailmentOptions,
  interruptionTypeOptions,
  isAdmin,
  hasAllGroups,
  balancingBeginError,
  balancingBeginHelperText,
  siBeginError,
  siBeginHelperText,
}) => {
  const hasAnySiType = siTypes.some((t) => !!t);
  const currentSiType = watch(`siType_${index}`);

  const selectedCount = siTypes.filter(Boolean).length;
  const entriesLabel = selectedCount === 1 ? "Entry" : "Entries";

  return (
    <Box sx={disabledFieldSx}>
      {/* ── Balancing Curtailment (only on first row) ── */}
      {index === 0 && (
        <>
          <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 1 }}>
            {/* Col 1: Label */}
            <Grid item xs={12} sm={3} md={3}>
              <Box sx={{ pt: 2 }}>
                <SectionLabel>Balancing Curtailment</SectionLabel>
              </Box>
            </Grid>

            {/* Col 2: Current value display */}
            <Grid item xs={12} sm={3} md={3}>
              <Box sx={{ pt: 2 }}>
                {!balancingCurtailment ? (
                  <Typography sx={{ color: "#5a7a9c" }}>
                    None Scheduled
                  </Typography>
                ) : (
                  <Typography sx={{ color: "#1a3a5c", fontWeight: 600 }}>
                    {curtailmentOptions.find((o) => o.value === balancingCurtailment)?.label ?? "—"}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Col 3: Curtailment type autocomplete */}
            <Grid item xs={12} sm={3} md={3}>
              <AutocompleteInput
                control={control}
                trigger={trigger}
                name="balancingCurtailment"
                label="Curtailment Type"
                options={curtailmentOptions}
                // Disabled when not admin OR when ALL GROUPS is selected
                disabled={!isAdmin || hasAllGroups}
              />
            </Grid>

            {/* Col 4: Begin / End date-time pickers */}
            <Grid item xs={12} sm={3} md={3}>
              <CustomDateTimePicker
                control={control}
                trigger={trigger}
                name="balancingBegin"
                label="Begin"
                // Disabled when no curtailment selected OR when not admin OR when ALL GROUPS is selected
                disabled={!balancingCurtailment || !isAdmin || hasAllGroups}
                // Required once a curtailment type is selected; surfaced after
                // the first Save & Send attempt and cleared live once filled in.
                error={!!balancingBeginError}
                helperText={balancingBeginHelperText}
              />
              <CustomDateTimePicker
                control={control}
                trigger={trigger}
                name="balancingEnd"
                label="End (optional)"
                disabled={!balancingCurtailment || !isAdmin || hasAllGroups}
              />
            </Grid>
          </Grid>

          <Divider sx={{ borderColor: "#d6e4f0", my: 1.5 }} />
        </>
      )}

      {/* ── Scheduled Interruption Row ── */}
      <Grid
        container
        spacing={2}
        alignItems="flex-start"
        sx={{ mb: isLast ? 0 : 1 }}
      >
        {/* Col 1: Label (only on first row) */}
        <Grid item xs={12} sm={3} md={3}>
          {index === 0 && (
            <Box sx={{ pt: 2 }}>
              <SectionLabel>Scheduled Interruption</SectionLabel>
            </Box>
          )}
        </Grid>

        {/* Col 2: Value summary (only on first row) */}
        <Grid item xs={12} sm={3} md={3}>
          {index === 0 && (
            <Box sx={{ pt: 2 }}>
              {!hasAnySiType ? (
                <Typography sx={{ color: "#5a7a9c" }}>
                  None Scheduled
                </Typography>
              ) : (
                <Typography sx={{ color: "#1a3a5c", fontWeight: 600 }}>
                  {selectedCount} {entriesLabel}
                </Typography>
              )}
            </Box>
          )}
        </Grid>

        {/* Col 3: Interruption type autocomplete */}
        <Grid item xs={12} sm={3} md={3}>
          <AutocompleteInput
            control={control}
            trigger={trigger}
            name={`siType_${index}`}
            label="Interruption Type"
            options={interruptionTypeOptions}
            // Disabled when not admin
            disabled={!isAdmin}
            onChange={(e, newValue) => {
              if (!newValue && isAdmin) {
                setValue(`siBegin_${index}`, null);
                setValue(`siEnd_${index}`, null);
              }
            }}
          />
        </Grid>

        {/* Col 4: Dates + Add/Remove buttons */}
        <Grid item xs={12} sm={3} md={3}>
          <CustomDateTimePicker
            control={control}
            trigger={trigger}
            name={`siBegin_${index}`}
            label="Begin"
            // Disabled when no SI type selected OR when not admin
            disabled={!currentSiType || !isAdmin}
            // Required once an interruption type is selected on this row;
            // surfaced after the first Save & Send attempt and cleared live
            // once filled in.
            error={!!siBeginError}
            helperText={siBeginHelperText}
          />
          <CustomDateTimePicker
            control={control}
            trigger={trigger}
            name={`siEnd_${index}`}
            label="End (optional)"
            disabled={!currentSiType || !isAdmin}
          />
          {/* Add/Remove buttons hidden when not admin */}
          {isAdmin && (
            <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
              {isLast && (
                <Tooltip title="Add Entry">
                  <IconButton size="small" onClick={onAdd} sx={{ color: "#1976d2" }}>
                    <AddCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {siCount > 1 && (
                <Tooltip title="Remove Entry">
                  <IconButton size="small" onClick={onRemove} sx={{ color: "#c0392b" }}>
                    <RemoveCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ScheduledInterruptionRow;